import { motion } from 'framer-motion';
import { Play } from 'lucide-react';
import TrackedBookingLink from './TrackedBookingLink';

interface HeroSectionProps {
  title: string;
  subtitle?: string;
  imageUrl?: string;
  primaryButton?: {
    text: string;
    href: string;
    external?: boolean;
  };
  secondaryButton?: {
    text: string;
    href: string;
    external?: boolean;
  };
}

export default function HeroSection({
  title,
  primaryButton,
  secondaryButton,
}: HeroSectionProps) {
  return (
    <div className="relative h-screen min-h-[700px] flex items-center justify-center bg-brand-black overflow-hidden">
      {/* Cinematic Background Section */}
      <div className="absolute inset-0 z-0 flex items-center justify-center">
        <div className="relative w-full h-full flex items-center justify-center">
          {/* Mobile Hero Image */}
          <div className="absolute inset-0 block md:hidden">
            <motion.img 
              initial={{ filter: 'grayscale(0%) blur(0px)' }}
              animate={{ filter: 'grayscale(100%) blur(0px)' }}
              transition={{ duration: 2, delay: 1.5 }}
              src="/images/hero-mobile.webp" 
              alt="Delta BJJ Mobile Hero" 
              className="w-full h-full object-cover"
            />
            {/* Overlays for Mobile (Full Screen) */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 2, delay: 1.5 }}
              className="absolute inset-0 bg-gradient-to-b from-brand-black/40 via-brand-black/60 to-brand-black z-10" 
            />
          </div>

          {/* Desktop Video / Image Container - Shrinks to fit uncropped content */}
          <div className="relative hidden md:flex items-center justify-center w-full h-full">
            {/* Blurred Background Layer to fill black bars */}
            <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
              <motion.video
                initial={{ filter: 'grayscale(0%) blur(40px)' }}
                animate={{ filter: 'grayscale(100%) blur(40px)' }}
                transition={{ duration: 2, delay: 1.5 }}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover scale-110 opacity-50"
                poster="/images/hero-team.jpg"
              >
                <source src="https://assets.mixkit.co/videos/preview/mixkit-man-training-on-the-beach-at-sunset-4028-large.mp4" type="video/mp4" />
              </motion.video>
            </div>

            {/* Main Foreground Content */}
            <div className="relative z-10 flex items-center justify-center max-w-full max-h-full w-full h-full">
              <motion.video
                initial={{ filter: 'grayscale(0%) blur(0px)' }}
                animate={{ filter: 'grayscale(100%) blur(0px)' }}
                transition={{ duration: 2, delay: 1.5 }}
                autoPlay
                loop
                muted
                playsInline
                className="max-w-full max-h-[100vh] w-auto h-auto object-contain block relative z-10"
                poster="/images/hero-team.jpg"
              >
                <source src="https://assets.mixkit.co/videos/preview/mixkit-man-training-on-the-beach-at-sunset-4028-large.mp4" type="video/mp4" />
              </motion.video>

              {/* Overlays - Constrained to the video size area */}
              <div className="absolute inset-0 pointer-events-none z-20 flex items-center justify-center">
                <div className="relative w-full h-full">
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 2, delay: 1.5 }}
                    className="absolute inset-0 bg-gradient-to-b from-brand-black/40 via-brand-black/60 to-brand-black" 
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-20 container mx-auto px-4 sm:px-6 lg:px-8 text-center flex items-end justify-center min-h-screen pb-32 md:pb-48">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.5, ease: "easeOut" }}
          className="max-w-6xl w-full mx-auto"
        >
          <motion.span 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.7, duration: 1 }}
            className="inline-block text-white font-display text-sm tracking-[0.4em] uppercase mb-8 drop-shadow-md"
          >
            EST. 1998 • CULVER CITY, CA
          </motion.span>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-display font-extrabold text-white mb-10 leading-none tracking-tighter">
            <span className="block">DELTA</span>
            <span className="blue-gradient-text"> BRAZILIAN JIU-JITSU</span>
          </h1>

          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.9, duration: 1 }}
            className="text-xl md:text-2xl text-white/80 max-w-2xl mx-auto mb-12 font-semibold tracking-wide uppercase"
          >
            {title}
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.1, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
          >
            {primaryButton && (
              <TrackedBookingLink
                href={primaryButton.href}
                className="btn-premium-blue w-full sm:w-auto flex items-center justify-center gap-2 group text-sm md:text-base font-extrabold"
              >
                <span>{primaryButton.text}</span>
                <Play className="w-4 h-4 fill-current group-hover:translate-x-1 transition-transform" />
              </TrackedBookingLink>
            )}
            
            {secondaryButton && (
              <TrackedBookingLink
                href={secondaryButton.href}
                className="btn-premium-outline w-full sm:w-auto text-sm md:text-base font-extrabold"
              >
                {secondaryButton.text}
              </TrackedBookingLink>
            )}
          </motion.div>
        </motion.div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-4">
        <span className="text-white/30 text-[10px] tracking-[0.3em] uppercase rotate-90 origin-left translate-x-1">Scroll</span>
        <motion.div 
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="w-[1px] h-12 bg-gradient-to-b from-brand-blue to-transparent"
        />
      </div>
    </div>
  );
}

